import React, {useState, useEffect, useCallback} from 'react';
import {
    Container,
    TextField,
    Button,
    Snackbar,
    Alert,
    Typography,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import axiosInstance from '../AxiosInstance';

function ProfilePage() {
    const navigate = useNavigate();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('Done.');

    const [mfaStatus, setMfaStatus] = useState(null);
    const [mfaLoadError, setMfaLoadError] = useState('');
    const [regenOpen, setRegenOpen] = useState(false);
    const [regenCode, setRegenCode] = useState('');
    const [newRecoveryCodes, setNewRecoveryCodes] = useState(null);
    const [codesDialogOpen, setCodesDialogOpen] = useState(false);

    const loadMfaStatus = useCallback(async () => {
        try {
            const {data} = await axiosInstance.get('/mfa/status');
            setMfaStatus(data);
            setMfaLoadError('');
        } catch (e) {
            setMfaLoadError(e.response?.data?.message || 'Could not load MFA status.');
        }
    }, []);

    useEffect(() => {
        loadMfaStatus();
    }, [loadMfaStatus]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) {
            setErrorMessage('New passwords do not match.');
            setErrorSnackbarOpen(true);
            return;
        }

        try {
            const response = await axiosInstance.post('/auth/change-password', {
                oldPassword,
                newPassword,
            });

            if (response.status === 200) {
                setSuccessMessage('Password changed successfully.');
                setSuccessSnackbarOpen(true);
                setOldPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || 'Failed to change password.');
            setErrorSnackbarOpen(true);
        }
    };

    const handleRegenCodes = async () => {
        try {
            const {data} = await axiosInstance.post('/mfa/recovery-codes/regenerate', {code: regenCode.trim()});
            setRegenOpen(false);
            setRegenCode('');
            setNewRecoveryCodes(data.recoveryCodes || []);
            setCodesDialogOpen(true);
            await loadMfaStatus();
        } catch (e) {
            setErrorMessage(e.response?.data?.message || 'Failed to regenerate codes.');
            setErrorSnackbarOpen(true);
        }
    };

    const graceDeadline =
        mfaStatus?.graceEndsAtUtc && !mfaStatus.enabled
            ? new Date(mfaStatus.graceEndsAtUtc).toLocaleString()
            : null;

    return (
        <Box className="component-container">
            <Container component="main" maxWidth="sm">
                <Paper elevation={3} sx={{p: 2.5, mb: 3}}>
                    <Typography component="h1" variant="h5" gutterBottom>
                        Two-factor authentication
                    </Typography>
                    {mfaLoadError ? (
                        <Alert severity="warning" sx={{mb: 2}}>{mfaLoadError}</Alert>
                    ) : mfaStatus ? (
                        <>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Status:{' '}
                                <strong>{mfaStatus.enabled ? 'Enabled' : 'Not enabled'}</strong>
                                {mfaStatus.recoveryCodesRemaining != null && mfaStatus.enabled ? (
                                    <> — Recovery codes remaining: {mfaStatus.recoveryCodesRemaining}</>
                                ) : null}
                            </Typography>
                            {graceDeadline ? (
                                <Alert severity="info" sx={{mb: 2}}>
                                    Please enable two-factor authentication before {graceDeadline}.
                                </Alert>
                            ) : null}
                            {!mfaStatus.enabled ? (
                                <Button variant="contained" color="primary" onClick={() => navigate('/settings/mfa-setup')}>
                                    Set up authenticator
                                </Button>
                            ) : (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                    <Button variant="outlined" onClick={() => setRegenOpen(true)}>
                                        Regenerate recovery codes
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2">Loading…</Typography>
                    )}
                </Paper>

                <Paper elevation={3} sx={{p: 2.5}}>
                    <Typography component="h2" variant="h5" gutterBottom>
                        Change password
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="Old password"
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="New password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="Confirm new password"
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            inputProps={{
                                onPaste: (e) => e.preventDefault(),
                            }}
                        />
                        <Button type="submit" fullWidth variant="contained" color="primary" sx={{mt: 3}}>
                            Change password
                        </Button>
                    </form>
                </Paper>

                <Dialog open={regenOpen} onClose={() => setRegenOpen(false)}>
                    <DialogTitle>Regenerate recovery codes</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{mb: 2}}>
                            Enter a code from your authenticator app. Existing recovery codes will stop working.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Authenticator code"
                            value={regenCode}
                            onChange={(e) => setRegenCode(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRegenOpen(false)}>Cancel</Button>
                        <Button onClick={handleRegenCodes} variant="contained">
                            Regenerate
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={codesDialogOpen} onClose={() => setCodesDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>New recovery codes</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{mb: 2}}>
                            Save these codes in a safe place. You will only see these recovery codes once, and each can be used once.
                        </Typography>
                        <Box component="ul" sx={{fontFamily: 'monospace', pl: 3}}>
                            {(newRecoveryCodes || []).map((c) => (
                                <li key={c}>{c}</li>
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCodesDialogOpen(false)} variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={errorSnackbarOpen} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                          autoHideDuration={6000} onClose={() => setErrorSnackbarOpen(false)}>
                    <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" sx={{width: '100%'}}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
                <Snackbar open={successSnackbarOpen} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                          autoHideDuration={6000} onClose={() => setSuccessSnackbarOpen(false)}>
                    <Alert onClose={() => setSuccessSnackbarOpen(false)} severity="success" sx={{width: '100%'}}>
                        {successMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}

export default ProfilePage;
